import type { FC } from 'react';
import { Col, Divider, Row } from 'antd';

import { DEFAULT_DATE_FORMAT } from '@/contants/common';
import { formatDate } from '@/utils/date';

import { formateAmount } from '../../../../../utilz';
import { convertUtcToLocal, getDateLabel } from '../../../../../utilz/dates';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import SecondaryLabel from '../../../../components/Labels/SecondaryLabel';
import { LOSS_AND_DAMAGE_COMMENT_TYPE } from '../../constants';
import type { LossAndDamageClaimPrintFormInfo } from '../../types';

interface LADPrintClaimProps {
  claimData: LossAndDamageClaimPrintFormInfo;
}

const LADPrintClaim: FC<LADPrintClaimProps> = ({ claimData }) => {
  return (
    <div className="f-14">
      <div className="text-center mb-20">
        <PrimaryLabel className="text-black">PRESENTATION OF LOSS AND DAMAGE CLAIM</PrimaryLabel>
      </div>
      <Row gutter={30}>
        <Col sm={12}>
          <div>
            <SecondaryLabel className="fw-700 text-black f-14">Claimant:</SecondaryLabel>
            <br />
            <SecondaryLabel className="text-black">{claimData.customerName}</SecondaryLabel>
          </div>
          <div className="mb-20">
            <SecondaryLabel className="fw-700 text-black f-14">{claimData.customerDetailName}</SecondaryLabel>
            <br />
            <SecondaryLabel className="text-black">{claimData.customerDetailAddress}</SecondaryLabel>
            <br />
            <SecondaryLabel className="text-black">{claimData.customerDetailCityStateZip}</SecondaryLabel>
            <br />
            <SecondaryLabel className="text-black">{claimData.customerDetailPhone}</SecondaryLabel>
          </div>
          <div>
            <SecondaryLabel className="fw-700 text-black f-15">
              FORWARD CORRESPONDENCE TO ADDRESS ABOVE AND MAKE CHECK PAYABLE TO :
            </SecondaryLabel>
            <br />
            <SecondaryLabel className="text-black f-15">{claimData.checkPayableTo}</SecondaryLabel>
          </div>
        </Col>
        <Col sm={12}>
          <SecondaryLabel className="fw-700 text-black f-14">General Claim Information :</SecondaryLabel>
          <div>
            <table className="padding-only-table">
              <tr>
                <td>
                  <SecondaryLabel className="fw-700 text-black f-14">Claim Number :</SecondaryLabel>
                </td>
                <td>
                  <SecondaryLabel className="text-black f-14">{claimData?.claimNumber}</SecondaryLabel>
                </td>
              </tr>
              <tr>
                <td>
                  <SecondaryLabel className="fw-700 text-black f-14">Date Filed :</SecondaryLabel>
                </td>
                <td>
                  <SecondaryLabel className="text-black f-14">
                    {formatDate(claimData?.dateFiled, DEFAULT_DATE_FORMAT)}
                  </SecondaryLabel>
                </td>
              </tr>
              <tr>
                <td>
                  <SecondaryLabel className="fw-700 text-black f-14">Claim Amount :</SecondaryLabel>
                </td>
                <td>
                  <SecondaryLabel className="text-black f-14">
                    {claimData.claimTotal ? Number(claimData.claimTotal).toFixed(2) : ''}
                  </SecondaryLabel>
                </td>
              </tr>
              <tr>
                <td>
                  <SecondaryLabel className="fw-700 text-black f-14">Type :</SecondaryLabel>
                </td>
                <td>
                  <SecondaryLabel className="text-black f-14">{claimData?.claimTypeDesc}</SecondaryLabel>
                </td>
              </tr>
              <tr>
                <td>
                  <SecondaryLabel className="fw-700 text-black f-14">Invoice Number :</SecondaryLabel>
                </td>
                <td>
                  <SecondaryLabel className="text-black f-14">{claimData?.invoiceNo}</SecondaryLabel>
                </td>
              </tr>
              <tr>
                <td>
                  <SecondaryLabel className="fw-700 text-black f-14">Accounting Code :</SecondaryLabel>
                </td>
                <td>
                  <SecondaryLabel className="text-black f-14">{claimData?.acctCode}</SecondaryLabel>
                </td>
              </tr>
              <tr>
                <td>
                  <SecondaryLabel className="fw-700 text-black f-14">A/P Number :</SecondaryLabel>
                </td>
                <td>
                  <SecondaryLabel className="text-black f-14">{claimData?.apNumber}</SecondaryLabel>
                </td>
              </tr>
              <tr>
                <td>
                  <SecondaryLabel className="fw-700 text-black f-14">Branch :</SecondaryLabel>
                </td>
                <td>
                  <SecondaryLabel className="text-black f-14">{claimData?.branchDesc}</SecondaryLabel>
                </td>
              </tr>
            </table>
          </div>
        </Col>
      </Row>
      <Row gutter={30} className="my-20">
        <Col sm={12}>
          <SecondaryLabel className="fw-700 text-black f-14">
            Transportation Provider's Name and Address :
          </SecondaryLabel>
          <br />
          <SecondaryLabel className="text-black f-14">{claimData?.carrierName}</SecondaryLabel>
          <br />
          <SecondaryLabel className="text-black f-14">{claimData?.carrierAddr1}</SecondaryLabel>
          <br />
          <SecondaryLabel className="text-black f-14">{claimData?.carrierAddr2}</SecondaryLabel>
          <br />
          <SecondaryLabel className="text-black f-14">{claimData?.carrierCSZ}</SecondaryLabel>
        </Col>
        <Col sm={12}>
          <SecondaryLabel className="fw-700 text-black f-14">Consignee's Name and Address :</SecondaryLabel>
          <br />
          <SecondaryLabel className="text-black f-14">{claimData?.consigneeName}</SecondaryLabel>
          <br />
          <SecondaryLabel className="text-black f-14">{claimData?.consigneeAddr1}</SecondaryLabel>
          <br />
          {claimData?.consigneeAddr2 && (
            <>
              <SecondaryLabel className="text-black f-14">{claimData?.consigneeAddr1}</SecondaryLabel>
              <br />
            </>
          )}
          <SecondaryLabel className="text-black f-14">
            {claimData?.consigneeCity}, {claimData?.consigneeState} {claimData?.consigneeZip}
          </SecondaryLabel>
        </Col>
      </Row>
      <Row gutter={30} align="middle">
        <Col sm={12}>
          <table className="padding-only-table">
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">Pro Number :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{claimData?.claimProNo}</SecondaryLabel>
              </td>
              <td className="pl-20">
                <SecondaryLabel className="fw-700 text-black f-14">Seal Number :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{claimData?.sealNumber}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">Date Shipped :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">
                  {formatDate(claimData?.dateShipped, DEFAULT_DATE_FORMAT)}
                </SecondaryLabel>
              </td>
              <td className="pl-20">
                <SecondaryLabel className="fw-700 text-black f-14">Seal Intact :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{claimData?.sealIntact}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">
                  Transportation <br /> Provider's Ack <br />
                  Number :
                </SecondaryLabel>
              </td>
              <td colSpan={3}>
                <SecondaryLabel className="text-black f-14">{claimData?.claimAckNo}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">Pieces :</SecondaryLabel>
              </td>
              <td colSpan={3}>
                <SecondaryLabel className="text-black f-14">{claimData?.claimNoOfPieces}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">Chargeable Weight :</SecondaryLabel>
              </td>
              <td colSpan={3}>
                <SecondaryLabel className="text-black f-14">
                  {formateAmount(claimData?.cargoShipmentWeight)}
                </SecondaryLabel>
              </td>
            </tr>
          </table>
        </Col>
        <Col sm={12}>
          <div>
            <SecondaryLabel className="fw-700 text-black f-14">Shipper's Name and Address :</SecondaryLabel>
            <br />
            <SecondaryLabel className="text-black f-14">
              {claimData?.shipperName}
              <br />
              {claimData?.shipperAddr1}
              <br />
              {claimData?.shipperAddr2 && (
                <>
                  {claimData?.shipperAddr2}
                  <br />
                </>
              )}
              {claimData?.shipperCity}, {claimData?.shipperState} {claimData?.shipperZip}
            </SecondaryLabel>
          </div>
        </Col>
      </Row>
      <div className="text-center">
        <PrimaryLabel className="text-black f-14">*****DETAILED CLAIM STATEMENT*****</PrimaryLabel>
      </div>
      <table className="normal-bordered-table">
        <thead>
          <tr>
            <th rowSpan={2}>
              <SecondaryLabel className="fw-700 text-black f-14">Quantity</SecondaryLabel>
            </th>
            <th rowSpan={2}>
              <SecondaryLabel className="fw-700 text-black f-14">Product Code</SecondaryLabel>
            </th>
            <th rowSpan={2}>
              <SecondaryLabel className="fw-700 text-black f-14">Product Description</SecondaryLabel>
            </th>
            <th rowSpan={2}>
              <SecondaryLabel className="fw-700 text-black f-14">Unit Of Measure</SecondaryLabel>
            </th>
            <th colSpan={2}>
              <SecondaryLabel className="fw-700 text-black f-14">Chargeable Weight</SecondaryLabel>
            </th>
            <th colSpan={3}>
              <SecondaryLabel className="fw-700 text-black f-14">Value</SecondaryLabel>
            </th>
            <th colSpan={2}>
              <SecondaryLabel className="fw-700 text-black f-14">Mitigated</SecondaryLabel>
            </th>
            <th rowSpan={2}>
              <SecondaryLabel className="fw-700 text-black f-14">Amount</SecondaryLabel>
            </th>
            <th rowSpan={2}>
              <SecondaryLabel className="fw-700 text-black f-14">Claim Amount</SecondaryLabel>
            </th>
          </tr>
          <tr>
            <th>
              <SecondaryLabel className="fw-700 text-black f-14">Item Unit Weight</SecondaryLabel>
            </th>
            <th>
              <SecondaryLabel className="fw-700 text-black f-14">Total Weight</SecondaryLabel>
            </th>
            <th>
              <SecondaryLabel className="fw-700 text-black f-14">Full/Release Value</SecondaryLabel>
            </th>
            <th>
              <SecondaryLabel className="fw-700 text-black f-14">Value/Each</SecondaryLabel>
            </th>
            <th>
              <SecondaryLabel className="fw-700 text-black f-14">Total Value</SecondaryLabel>
            </th>
            <th>
              <SecondaryLabel className="fw-700 text-black f-14">Percentage/Value</SecondaryLabel>
            </th>
            <th>
              <SecondaryLabel className="fw-700 text-black f-14">Type</SecondaryLabel>
            </th>
          </tr>
        </thead>
        <tbody>
          {claimData.claimItem
            ? claimData.claimItem.map((item) => (
                <tr key={item.itemSeq}>
                  <td>
                    <SecondaryLabel className="text-black f-14">{item?.itemQuantity}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{item?.itemCode}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{item?.itemDesc}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{item?.uomDesc}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{formateAmount(item?.itemUnitWeight)}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{formateAmount(item?.totalWeight)}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">
                      {item?.fullRelFlag === 'F' ? 'Full' : 'Release'}
                    </SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{formateAmount(item?.value)}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{formateAmount(item?.totalValue)}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">
                      {item.smType === 'P' && item.smAmount != null && item.smAmount > 0
                        ? '%'
                        : item.smType === 'A' && item.smAmount != null && item.smAmount > 0
                          ? '$'
                          : ''}
                    </SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{item?.smFlag}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">{formateAmount(item?.smAmount)}</SecondaryLabel>
                  </td>
                  <td>
                    <SecondaryLabel className="text-black f-14">
                      {formateAmount(item?.productClaimAmount)}
                    </SecondaryLabel>
                  </td>
                </tr>
              ))
            : null}
        </tbody>
      </table>
      <Row justify="space-between" className="my-15">
        <Col sm={12}>
          <SecondaryLabel className="fw-700 text-black f-14">Notes :</SecondaryLabel>
          <div className="mt-10">
            {claimData?.claimComment
              ?.filter((note) => note.commentType !== LOSS_AND_DAMAGE_COMMENT_TYPE['In-House'])
              .map((note) => (
                <Row key={note.sequenceNo} gutter={15} className="mb-10">
                  <Col>
                    <SecondaryLabel className="text-black f-14">
                      {getDateLabel(convertUtcToLocal(note.createdOn))}
                    </SecondaryLabel>
                  </Col>
                  <Col>
                    <SecondaryLabel className="text-black f-14">{note.commentText}</SecondaryLabel>
                  </Col>
                </Row>
              ))}
          </div>
        </Col>
        <Col>
          <table>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">+ Freight Cost (pro-rated):</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">
                  {formateAmount(claimData?.freightChargeClaimed)}
                </SecondaryLabel>
              </td>
            </tr>

            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">
                  + Labor Cost ({Number(claimData?.laborHours).toFixed(2)} Hours):
                </SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{formateAmount(claimData?.laborCharge)}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">+ Additional Cost :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{formateAmount(claimData?.claimAddCharge)}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">
                  + Inspection Cost ({Number(claimData?.inspectionHours).toFixed(2)} Hours):
                </SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{formateAmount(claimData?.inspectionCost)}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">
                  + Interest Expense ({Number(`${claimData?.claimInterestRate}1`).toFixed(2)} %):
                </SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{formateAmount(claimData?.interestExpense)}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">+ Documentation Cost :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">
                  {formateAmount(claimData?.claimAdminCharge)}
                </SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">+ Taxes :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{formateAmount(claimData?.claimTaxAmount)}</SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">- Deductible Amount :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">
                  {formateAmount(claimData?.claimDeductibleAmount)}
                </SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">- Credit Amount :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">
                  {formateAmount(claimData?.claimCreditAmount)}
                </SecondaryLabel>
              </td>
            </tr>
            <tr>
              <td>
                <SecondaryLabel className="fw-700 text-black f-14">CLAIM AMOUNT DUE :</SecondaryLabel>
              </td>
              <td>
                <SecondaryLabel className="text-black f-14">{formateAmount(claimData?.claimTotal)}</SecondaryLabel>{' '}
                <SecondaryLabel className="text-black f-14">{claimData?.currencyCode}</SecondaryLabel>
              </td>
            </tr>
          </table>
        </Col>
      </Row>
      <Divider className="m-0 border-top-black" />
      <SecondaryLabel className="fw-700 text-black f-14">
        INDEMNITY AGREEMENT: The claimant agrees to protect the Transportation Provider and its connections against any
        loss from non-surrender Of the Original Freight Bill, Original Bill Of Lading, or Both.
      </SecondaryLabel>
      <Row className="border-black">
        <Col sm={12} className="border-right-black p-5">
          <SecondaryLabel className="fw-700 text-black f-12">
            The following documents are submitted in support of this claim : <br /> __ Bill of Lading ______ Freight
            Bill ______ Invoice ______ Other _____________________________ :
          </SecondaryLabel>
        </Col>
        <Col sm={12} className="p-5">
          <SecondaryLabel className="fw-700 text-black f-12">
            The foregoing statement of facts is hereby certified to be correct :
          </SecondaryLabel>
          <br />
          <SecondaryLabel className="ml-20 fw-700 text-black f-12">
            Claim Prepared By {claimData?.preparedBy} ({claimData?.emailOfPreparedBy})
          </SecondaryLabel>
        </Col>
      </Row>
    </div>
  );
};

export default LADPrintClaim;
